import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ConstructionIcon from "@mui/icons-material/Construction";
import DownloadIcon from "@mui/icons-material/Download";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ScienceIcon from "@mui/icons-material/Science";
import SecurityIcon from "@mui/icons-material/Security";

import { axiosInstance } from "../../../../api/axios";

const QUICK_ACTIONS = [
  {
    title: "평가 시작",
    path: "/assessment/wizard",
    color: "#2563eb",
    icon: <PlayArrowIcon />,
  },
  {
    title: "보고서 다운로드",
    path: "/monitoring/integrated-report",
    color: "#4f46e5",
    icon: <DownloadIcon />,
  },
  {
    title: "공정 관리",
    path: "/standard/process",
    color: "#0f766e",
    icon: <ConstructionIcon />,
  },
  {
    title: "설비 관리",
    path: "/standard/machine",
    color: "#0284c7",
    icon: <PrecisionManufacturingIcon />,
  },
  {
    title: "위험요인 관리",
    path: "/register/cause",
    color: "#ea580c",
    icon: <ReportProblemIcon />,
  },
  {
    title: "MSDS 관리",
    path: "/standard/msds",
    color: "#7c3aed",
    icon: <ScienceIcon />,
  },
  {
    title: "TBM 관리",
    path: "/monitoring/tbm",
    color: "#0891b2",
    icon: <Inventory2Icon />,
  },
  {
    title: "안전점검",
    path: "/monitoring/inspection",
    color: "#dc2626",
    icon: <SecurityIcon />,
  },
  {
    title: "교육 일정",
    path: "/monitoring/education",
    color: "#4f46e5",
    icon: <CalendarMonthIcon />,
  },
  {
    title: "제안 검토",
    path: "/worker/hazard-review",
    color: "#06b6d4",
    icon: <AssignmentTurnedInIcon />,
  },
];

const emptyDashboard = {
  pendingHazards: 0,
  approvalPending: 0,
  approvalCompleted: 0,
  improveWaiting: 0,
  improveOverdue: 0,
  improveCompleted: 0,
  pendingProposals: 0,
  monthlyCompletion: [],
  upcomingEducation: [],
  upcomingTbm: [],
  isLoading: true,
  isError: false,
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isCompletedImprove = (row) => toNumber(row?.improve_status) >= 20;
const isOverdueImprove = (row) => (
  row?.expected_date &&
  !isCompletedImprove(row) &&
  new Date(row.expected_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
);

const formatDateParam = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getRecentMonths = () => {
  const current = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(current.getFullYear(), current.getMonth() - (5 - index), 1);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: `${date.getMonth() + 1}월`,
    };
  });
};

const panelSx = {
  border: "1px solid",
  borderColor: "#e5e7eb",
  borderRadius: "8px",
  bgcolor: "#fff",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
};

const tallPanelSx = {
  minHeight: 0,
};

const lowerPanelSx = {
  minHeight: 0,
};

function SectionPanel({ title, children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: { xs: 2, md: 2 },
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {children}
      </Box>
    </Paper>
  );
}

function KpiBoard({ items }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 0,
        width: "100%",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        {items.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              minHeight: { xs: 112, md: 124, lg: 118 },
              p: { xs: 2, md: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              borderRight: {
                lg: index < items.length - 1 ? "1px solid #eef2f7" : "none",
              },
              borderBottom: {
                xs: index < 2 ? "1px solid #eef2f7" : "none",
                lg: "none",
              },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.25} sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  color: item.color,
                  bgcolor: "#f8fafc",
                  border: "1px solid",
                  borderColor: "#e5e7eb",
                }}
              >
                {item.icon}
              </Avatar>
              <Typography sx={{ fontWeight: 900, color: "#334155", lineHeight: 1.2, textAlign: "center" }} noWrap>
                {item.label}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
              <Typography
                sx={{
                  fontSize: { xs: 32, md: 36 },
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: "#0f172a",
                }}
              >
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: "#6b7280" }}>건</Typography>
            </Stack>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function QuickActionCard({ item, onClick }) {
  return (
    <Paper
      component="button"
      type="button"
      elevation={0}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: { xs: 104, md: 86, xl: 94 },
        p: { xs: 1.75, md: 1.45 },
        border: "1px solid",
        borderColor: "#e5e7eb",
        borderRadius: "8px",
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "center",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "#cbd5e1",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.055)",
          transform: "translateY(-1px)",
        },
        "&:focus-visible": {
          outline: "2px solid #2563eb",
          outlineOffset: 2,
        },
      }}
    >
      <Stack spacing={0.9} alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
        <Avatar
          sx={{
            width: { xs: 42, md: 36, xl: 40 },
            height: { xs: 42, md: 36, xl: 40 },
            color: item.color,
            bgcolor: "#f8fafc",
            border: "1px solid",
            borderColor: "#e5e7eb",
          }}
        >
          {item.icon}
        </Avatar>
        <Typography sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.2, textAlign: "center" }}>
          {item.title}
        </Typography>
      </Stack>
    </Paper>
  );
}

function MonthlyChart({ data }) {
  const maxValue = Math.max(1, ...data.map((item) => item.value));

  return (
    <SectionPanel title="월별 개선 완료 현황" sx={lowerPanelSx}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="end"
        sx={{ flex: 1, minHeight: { xs: 190, lg: 145, xl: 170 } }}
      >
        {data.map((item, index) => {
          const height = Math.max(30, Math.round((item.value / maxValue) * 120));
          const isCurrent = index === data.length - 1;
          return (
            <Stack key={`${item.year}-${item.month}`} spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {item.value}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 34,
                  height,
                  borderRadius: "7px 7px 3px 3px",
                  bgcolor: isCurrent ? "#334155" : "#e5e7eb",
                }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </SectionPanel>
  );
}

const scheduleStatusMeta = {
  pending: { label: "예정", color: "primary" },
  completed: { label: "완료", color: "success" },
  cancelled: { label: "취소", color: "default" },
  unregistered: { label: "미등록", color: "warning" },
  missed: { label: "미실시", color: "error" },
};

function ScheduleList({ icon, title, items, emptyText, renderPrimary, renderSecondary, renderChip }) {
  return (
    <Stack spacing={1.1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: "#f8fafc",
            color: "#475569",
            border: "1px solid #e5e7eb",
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 900, color: "#111827" }}>
          {title}
        </Typography>
      </Stack>

      <Stack spacing={0.75}>
        {items.map((item) => (
          <Box
            key={`${title}-${item.id}`}
            sx={{
              p: { xs: 1.15, lg: 0.95 },
              border: "1px solid #eef2f7",
              borderRadius: "8px",
              bgcolor: "#fbfdff",
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: "#0f172a" }} noWrap>
                  {renderPrimary(item)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }} noWrap>
                  {renderSecondary(item)}
                </Typography>
              </Box>
              {renderChip(item)}
            </Stack>
          </Box>
        ))}

        {!items.length ? (
          <Box
            sx={{
              px: 1.25,
              py: { xs: 1.15, lg: 0.9 },
              border: "1px dashed #dbe3ef",
              borderRadius: "8px",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800 }}>
              {emptyText}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}

function UpcomingSchedulePanel({ educationItems, tbmItems, isLoading }) {
  return (
    <SectionPanel title="다가오는 일정" sx={lowerPanelSx}>
      <Stack spacing={{ xs: 1.6, lg: 1.15 }} divider={<Divider flexItem />} sx={{ flex: 1 }}>
        <ScheduleList
          icon={<CalendarMonthIcon fontSize="small" />}
          title="안전보건교육"
          items={educationItems.slice(0, 2)}
          emptyText={isLoading ? "교육 일정을 불러오는 중입니다" : "다가오는 교육 일정이 없습니다"}
          renderPrimary={(item) => item.title || "-"}
          renderSecondary={(item) => `${item.edu_day || "-"} ${item.edu_time || ""}${item.target_desc ? ` · ${item.target_desc}` : ""}`}
          renderChip={(item) => {
            const meta = scheduleStatusMeta[item.status] || scheduleStatusMeta.pending;
            return <Chip size="small" color={meta.color} label={item.edu_type || meta.label} />;
          }}
        />

        <ScheduleList
          icon={<Inventory2Icon fontSize="small" />}
          title="TBM"
          items={tbmItems.slice(0, 2)}
          emptyText={isLoading ? "TBM 일정을 불러오는 중입니다" : "다가오는 TBM 일정이 없습니다"}
          renderPrimary={(item) => item.team_name || "-"}
          renderSecondary={(item) => `${item.session_day || "-"} ${item.session_time || ""}${item.leader_name ? ` · ${item.leader_name}` : ""}`}
          renderChip={(item) => {
            const meta = scheduleStatusMeta[item.status] || scheduleStatusMeta.unregistered;
            return <Chip size="small" color={meta.color} label={meta.label} />;
          }}
        />
      </Stack>
    </SectionPanel>
  );
}

function RecentActivityPanel({ activities }) {
  return (
    <SectionPanel title="후속 처리 알림" sx={tallPanelSx}>
      <Stack divider={<Divider flexItem />} spacing={0} sx={{ flex: 1 }}>
        {activities.map((activity) => (
          <Stack
            key={activity.title}
            direction="row"
            spacing={1.25}
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: { xs: 1.25, lg: 1.1 } }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                {activity.title}
              </Typography>
            </Box>
            <Chip size="small" color={activity.color} label={activity.type} />
          </Stack>
        ))}
      </Stack>
    </SectionPanel>
  );
}

function RiskQueuePanel({ items }) {
  return (
    <SectionPanel title="우선 처리 항목" sx={lowerPanelSx}>
      <Stack spacing={{ xs: 1.4, lg: 1.45 }} sx={{ flex: 1, justifyContent: "center" }}>
        {items.map((item) => (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.6 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {item.value}건
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={item.progress}
              sx={{
                height: 9,
                borderRadius: 999,
                bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": {
                  bgcolor: item.color,
                  borderRadius: 999,
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </SectionPanel>
  );
}

function RiskAssessmentMain() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [dashboard, setDashboard] = useState(emptyDashboard);

  const groupCode = group_code || "dev";
  const projectCode = project_code || "ese";
  const basePath = useMemo(
    () => `/safe/${groupCode}/${projectCode}`,
    [groupCode, projectCode]
  );

  useEffect(() => {
    let ignore = false;
    const current = new Date();
    const year = current.getFullYear();
    const month = current.getMonth() + 1;
    const months = getRecentMonths();
    const eduRangeEnd = addDays(current, 30);
    const tbmDates = Array.from({ length: 7 }, (_, index) => formatDateParam(addDays(current, index)));

    const loadDashboard = async () => {
      setDashboard((prev) => ({ ...prev, isLoading: true, isError: false }));

      const [
        approvalResult,
        pendingHazardResult,
        proposalResult,
        improveWaitingResult,
        improveCompletedResult,
        monthlyResults,
        upcomingEducationResult,
        upcomingTbmResult,
      ] = await Promise.all([
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/getRiskAssessmentApproval`),
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/wizard/pending-hazards`, {
          params: { eval_date: formatDateParam(current) },
        }),
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/worker-hazard-proposals`, {
          params: { status: "pending" },
        }),
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/getStrategyRegister`, {
          params: { year, month, machine_id: "all" },
        }),
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/getStrategyResult`, {
          params: { year, month, machine_id: "all", filter: "all" },
        }),
        Promise.all(
          months.map((item) =>
            axiosInstance
              .get(`/risk/${groupCode}/${projectCode}/getStrategyResult`, {
                params: { year: item.year, month: item.month, machine_id: "all", filter: "all" },
              })
              .then((response) => ({
                ...item,
                value: (response.data || []).filter(isCompletedImprove).length,
              }))
              .catch(() => ({ ...item, value: 0 }))
          )
        ),
        axiosInstance.get(`/risk/${groupCode}/${projectCode}/edu/schedules`, {
          params: {
            start: formatDateParam(current),
            end: formatDateParam(eduRangeEnd),
          },
        }),
        Promise.all(
          tbmDates.map((date) =>
            axiosInstance
              .get(`/risk/${groupCode}/${projectCode}/tbm/sessions`, { params: { date } })
              .then((response) => response.data || [])
              .catch(() => [])
          )
        ),
      ].map((promise) => promise.catch((error) => ({ error }))));

      if (ignore) return;

      const approvals = approvalResult.error ? [] : approvalResult.data || [];
      const pendingHazards = pendingHazardResult.error ? [] : pendingHazardResult.data?.rows || [];
      const pendingProposals = proposalResult.error ? [] : proposalResult.data || [];
      const improveWaitingRows = improveWaitingResult.error ? [] : improveWaitingResult.data || [];
      const improveCompletedRows = improveCompletedResult.error ? [] : improveCompletedResult.data || [];
      const monthlyCompletion = Array.isArray(monthlyResults) ? monthlyResults : months.map((item) => ({ ...item, value: 0 }));
      const upcomingEducation = upcomingEducationResult.error ? [] : upcomingEducationResult.data || [];
      const upcomingTbm = upcomingTbmResult.error
        ? []
        : (upcomingTbmResult || [])
            .flat()
            .sort((a, b) => String(a.session_date_input || "").localeCompare(String(b.session_date_input || "")));

      setDashboard({
        pendingHazards: pendingHazards.length,
        approvalPending: approvals.filter((row) => toNumber(row.eval_status) < 20).length,
        approvalCompleted: approvals.filter((row) => toNumber(row.eval_status) >= 20).length,
        improveWaiting: improveWaitingRows.filter((row) => !isCompletedImprove(row)).length,
        improveOverdue: improveWaitingRows.filter(isOverdueImprove).length,
        improveCompleted: improveCompletedRows.filter(isCompletedImprove).length,
        pendingProposals: pendingProposals.length,
        monthlyCompletion,
        upcomingEducation,
        upcomingTbm,
        isLoading: false,
        isError: Boolean(
          approvalResult.error ||
          pendingHazardResult.error ||
          proposalResult.error ||
          improveWaitingResult.error ||
          improveCompletedResult.error ||
          upcomingEducationResult.error ||
          upcomingTbmResult.error
        ),
      });
    };

    loadDashboard();

    return () => {
      ignore = true;
    };
  }, [groupCode, projectCode]);

  const kpiItems = [
    {
      label: "미평가 항목",
      value: dashboard.pendingHazards,
      color: "#2563eb",
      bg: "#eff6ff",
      icon: <AssessmentIcon />,
    },
    {
      label: "승인 대기",
      value: dashboard.approvalPending,
      color: "#f59e0b",
      bg: "#fffbeb",
      icon: <AssignmentTurnedInIcon />,
    },
    {
      label: "개선 대기",
      value: dashboard.improveWaiting,
      color: "#dc2626",
      bg: "#fef2f2",
      icon: <ReportProblemIcon />,
    },
    {
      label: "개선 완료",
      value: dashboard.improveCompleted,
      color: "#16a34a",
      bg: "#f0fdf4",
      icon: <FactCheckIcon />,
    },
  ];

  const queueItems = [
    { label: "개선 기한 초과", value: dashboard.improveOverdue, progress: Math.min(100, dashboard.improveOverdue * 18), color: "#dc2626" },
    { label: "작업자 제안 검토", value: dashboard.pendingProposals, progress: Math.min(100, dashboard.pendingProposals * 15), color: "#f59e0b" },
    { label: "평가 승인 대기", value: dashboard.approvalPending, progress: Math.min(100, dashboard.approvalPending * 12), color: "#2563eb" },
  ];

  const activities = [
    dashboard.approvalPending > 0 && {
      title: `승인 대기 평가 ${dashboard.approvalPending}건`,
      type: "평가",
      color: "warning",
    },
    dashboard.pendingProposals > 0 && {
      title: `작업자 제안 검토 ${dashboard.pendingProposals}건`,
      type: "제안",
      color: "info",
    },
    dashboard.improveOverdue > 0 && {
      title: `개선 기한 초과 ${dashboard.improveOverdue}건`,
      type: "개선",
      color: "error",
    },
    dashboard.pendingHazards > 0 && {
      title: `미평가 항목 ${dashboard.pendingHazards}건`,
      type: "평가",
      color: "primary",
    },
  ].filter(Boolean);

  const safeActivities = activities.length
    ? activities
    : [{
        title: "현재 즉시 처리할 알림이 없습니다",
        type: "정상",
        color: "success",
      }];

  const goTo = (path) => {
    navigate(`${basePath}${path}`);
  };

  return (
    <Box
      sx={{
        minHeight: { xs: "100%", lg: "calc(100vh - 60px)" },
        height: { xs: "auto", lg: "calc(100vh - 60px)" },
        p: { xs: 1.5, md: 3 },
        bgcolor: "#f3f6fa",
        overflow: { xs: "auto", lg: "hidden" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={{ xs: 2.5, lg: 1.5 }} sx={{ flex: 1, minHeight: 0 }}>
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: { xs: 2.25, md: 2 },
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 900, letterSpacing: 0 }}>
            위험성평가 관리 현황
          </Typography>
        </Paper>

        <KpiBoard
          items={kpiItems.map((item) =>
            dashboard.isLoading ? { ...item, value: "-" } : item
          )}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(12, minmax(0, 1fr))",
            },
            gridTemplateRows: {
              xs: "auto",
              lg: "minmax(250px, 1.08fr) minmax(210px, 0.92fr)",
            },
            gridAutoRows: { xs: "auto" },
            "& > *": {
              minHeight: 0,
              display: "flex",
            },
          }}
        >
          <Box sx={{ gridColumn: { xs: "1", lg: "1 / span 9" } }}>
            <SectionPanel title="주요 관리 기능" sx={tallPanelSx}>
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(5, minmax(0, 1fr))",
                  },
                  gridTemplateRows: { lg: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {QUICK_ACTIONS.map((item) => (
                  <QuickActionCard key={item.title} item={item} onClick={() => goTo(item.path)} />
                ))}
              </Box>
            </SectionPanel>
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "10 / span 3" } }}>
            <RecentActivityPanel activities={safeActivities} />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "1 / span 4" } }}>
            <MonthlyChart data={dashboard.monthlyCompletion.length ? dashboard.monthlyCompletion : getRecentMonths().map((item) => ({ ...item, value: 0 }))} />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "5 / span 4" } }}>
            <UpcomingSchedulePanel
              educationItems={dashboard.upcomingEducation}
              tbmItems={dashboard.upcomingTbm}
              isLoading={dashboard.isLoading}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "9 / span 4" } }}>
            <RiskQueuePanel items={queueItems} />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export { RiskAssessmentMain };
